import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { actionLabels, clientTargetTypes, targetTypeLabels } from "../constants";

const Table = styled.table`
  background: #fff;
  border-spacing: 0px;
  width: 100%;
`;

const TableContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 0px 8px 0px #e1e2e6;
  height: 700px;
  margin: 0 auto;
  overflow: auto;
  position: relative;
`;

const TableHead = styled.thead`
  color: #fff;
`;

const TableBody = styled.tbody``;

const TableHeadRow = styled.tr``;

const TableBodyRow = styled.tr`
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};

  &:hover {
    background: ${(props) => (props.clickable ? "#f2f2f2" : "transparent")};
  }

  ${(props) => props.expanded && `
    background: #f2f2f2;
  `}
`;

const HeadCell = styled.th`
  background: #070c29 !important;
  color: #fff;
  font-family: ${(props) => props.theme.font.primary};
  font-size: 0.9rem;
  font-weight: 500;
  letter-spacing: 1px;
  min-width: 120px;
  padding: 8px;
  position: sticky;
  text-align: left;
  top: 0;
  z-index: 30;
`;

const DataCell = styled.td`
  border-bottom: 1px solid #e8e8e8;
  font-family: ${(props) => props.theme.font.secondary};
  font-size: 0.9rem;
  padding: 8px;
  vertical-align: top;
`;

const ExpandedRow = styled.tr`
  background: #f8f9fb;
`;

const ExpandedCell = styled(DataCell)`
  background: #f8f9fb;
  border-bottom: ${(props) => (props.open ? "1px solid #e8e8e8" : "none")};
  padding: ${(props) => (props.open ? "0 16px 16px 16px" : "0 16px")};
`;

const ExpandContent = styled.div`
  max-height: ${(props) => (props.open ? "320px" : "0")};
  opacity: ${(props) => (props.open ? 1 : 0)};
  overflow: hidden;
  padding-top: ${(props) => (props.open ? "16px" : "0")};
  transition: max-height 0.25s ease, opacity 0.2s ease, padding-top 0.25s ease;
`;

const Status = styled.div`
  padding: 16px;
  color: #666;
  font-family: "DM Sans", sans-serif;
`;

const TableTitle = styled.h2`
  display: flex;
  align-items: flex-start;
  padding-bottom: 16px;
  margin: 20px auto 0 auto;
  color: #555;
  font-family: "DM Sans", sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
`;

const DetailSection = styled.div`
  color: #555;
  line-height: 1.5;
  margin-bottom: 12px;
  word-break: break-word;
`;

const TargetList = styled.ul`
  list-style: none;
  margin: 8px 0 0 0;
  padding: 0;
`;

const TargetListItem = styled.li`
  margin-bottom: 6px;
`;

const SectionHeader = styled.div`
  color: #555;
  font-family: "DM Sans", sans-serif;
  font-size: ${(props) => (props.details ? "1rem" : "0.9rem")};
  font-weight: 700;
`;

const metadataKeyLabels = {
  check_in_day: "Check-in Day",
  vacation_mode: "Mode",
  log_weekly_body_metrics: "Log Weekly Body Metrics",
  hide_protein: "Protein",
  hide_carbs: "Carbs",
  hide_fat: "Fat",
  linked_portal_id: "Linked Coach ID",
  previous_linked_portal_id: "Previous Linked Coach ID",
  link_action: "Link Action",
  schedule_id: "Schedule ID",
  schedule_type: "Schedule Type",
  schedule_date: "Schedule Date",
  actual_end_date: "Actual End Date",
  sent_clients: "Sent Clients",
  failed_clients: "Failed To Send",
  failures: "Failed To Send"
};

const getClientTargets = (targets = []) => targets.filter((target) => clientTargetTypes[target.type]);

const getNonClientTargets = (targets = []) => targets.filter((target) => !clientTargetTypes[target.type]);

const getTargetTypeLabel = (type) => targetTypeLabels[type] || "Target";

const getAssignmentPreposition = (actionType) => {
  if (!actionType) return "";
  if (actionType.includes("REMOVE") || actionType.includes("UNASSIGN")) return "from";
  if (actionType.includes("ASSIGN")) return "to";
  return "";
};

const getBaseTargetSummary = (targets = []) => {
  const clientTargets = getClientTargets(targets);

  if (clientTargets.length === 1) {
    return clientTargets[0].label || "Client";
  }

  if (clientTargets.length > 1) {
    return `${clientTargets.length} Clients`;
  }

  if (targets.length === 0) {
    return "No targets";
  }

  return targets[0].label || `${getTargetTypeLabel(targets[0].type)} ${targets[0].id || ""}`.trim();
};

const getTargetSummary = (log) => {
  const targets = Array.isArray(log?.targets) ? log.targets : [];
  const summary = getBaseTargetSummary(targets);
  const clientTargets = getClientTargets(targets);
  const nonClientTargets = getNonClientTargets(targets);
  const assignmentTarget = nonClientTargets[0];
  const preposition = getAssignmentPreposition(log?.action_type);

  if (clientTargets.length > 0 && assignmentTarget && preposition) {
    const assignmentLabel = assignmentTarget.label || `${getTargetTypeLabel(assignmentTarget.type)} ${assignmentTarget.id || ""}`.trim();
    return `${summary} ${preposition} ${assignmentLabel}`;
  }

  return summary;
};

const formatMetadataLabel = (key) => {
  if (metadataKeyLabels[key]) return metadataKeyLabels[key];

  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatVacationMode = (value, metadata = {}) => {
  const mode = Number(value);
  if (Number.isNaN(mode)) return String(value);
  if (mode === 0) return "Turn Off";
  if (mode === 1) return metadata.practice_mode ? "Practice Mode" : "Pause Mode";
  if (mode === 2) return "Pause Mode (Hide Macro Targets)";
  return `Mode ${mode}`;
};

const vacationMacroKeys = ["hide_protein", "hide_carbs", "hide_fat"];

const getMetadataEntries = (metadata) => {
  if (!metadata || typeof metadata !== "object") return [];

  const entries = [];

  if (Object.prototype.hasOwnProperty.call(metadata, "vacation_mode")) {
    entries.push(["vacation_mode", metadata.vacation_mode]);
  }

  if (Object.prototype.hasOwnProperty.call(metadata, "log_weekly_body_metrics")) {
    entries.push(["log_weekly_body_metrics", metadata.log_weekly_body_metrics]);
  }

  if (Object.prototype.hasOwnProperty.call(metadata, "hide_protein")) {
    entries.push(["hide_protein", metadata.hide_protein]);
  }
  if (Object.prototype.hasOwnProperty.call(metadata, "hide_carbs")) {
    entries.push(["hide_carbs", metadata.hide_carbs]);
  }
  if (Object.prototype.hasOwnProperty.call(metadata, "hide_fat")) {
    entries.push(["hide_fat", metadata.hide_fat]);
  }

  const hiddenKeys = new Set([
    "group_id",
    "form_id",
    "meal_template_id",
    "day_template_id",
    "vacation_mode",
    "practice_mode",
    "log_weekly_body_metrics",
    ...vacationMacroKeys
  ]);
  Object.entries(metadata).forEach(([key, value]) => {
    if (!hiddenKeys.has(key)) {
      entries.push([key, value]);
    }
  });

  return entries;
};

const formatMetadataValue = (key, value, metadata = {}) => {
  if (value === null || value === undefined) return "";
  if (
    key === "practice_mode" ||
    key === "log_weekly_body_metrics"
  ) {
    return value ? "Enabled" : "Disabled";
  }
  if (key === "hide_protein" || key === "hide_carbs" || key === "hide_fat") {
    return value ? "Hidden" : "Shown";
  }

  if (key === "vacation_mode") {
    return formatVacationMode(value, metadata);
  }

  if (typeof value === "object") return JSON.stringify(value);
  const str = String(value);
  if (key === "schedule_type") {
    if (str === "0") return "Assign client(s) to check-in form";
    if (str === "1") return "Delete client(s) from check-in form";
  }
  if (key === "schedule_date" && moment(str).isValid()) {
    return moment(str).format("MMM D, YYYY h:mm a");
  }
  if (key === "actual_end_date" && moment.utc(str, "YYYY-MM-DD", true).isValid()) {
    return moment.utc(str).format("MMM D, YYYY");
  }
  if ((key === "sent_clients" || key === "failed_clients") && Array.isArray(value)) {
    if (value.length === 0) return "";
    return value.join(", ");
  }
  if (key === "failures" && Array.isArray(value)) {
    const failedClientLabels = value
      .map((entry) => (Array.isArray(entry) ? entry[0] : entry))
      .filter(Boolean);
    return failedClientLabels.length > 0 ? failedClientLabels.join(", ") : "";
  }
  if (key === "check_in_day" && /^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const date = new Date(`${str}T12:00:00`);
    const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return weekdays[date.getDay()];
  }
  return str;
};

const LogTable = ({ logs, loading, error }) => {
  const [expandedLogId, setExpandedLogId] = useState(null);

  useEffect(() => {
    if (expandedLogId === null) return;
    if (!logs.some((log) => log.id === expandedLogId)) {
      setExpandedLogId(null);
    }
  }, [logs, expandedLogId]);

  const toggleExpandedRow = (logId) => {
    setExpandedLogId((current) => (current === logId ? null : logId));
  };

  return (
    <>
      <TableTitle>Logs</TableTitle>
      <TableContainer>
        {loading && <Status>Loading logs…</Status>}
        {error && <Status>{error}</Status>}
        {!loading && !error && (
          <Table>
            <TableHead>
              <TableHeadRow>
                <HeadCell>Timestamp</HeadCell>
                <HeadCell>Trainer</HeadCell>
                <HeadCell>Action</HeadCell>
                <HeadCell>Details</HeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableBodyRow>
                  <DataCell colSpan={4}>No logs found.</DataCell>
                </TableBodyRow>
              )}
              {logs.map((log) => {
                const isExpanded = expandedLogId === log.id;
                const targets = Array.isArray(log.targets) ? log.targets : [];
                const clientTargets = getClientTargets(targets);
                const nonClientTargets = getNonClientTargets(targets);
                const metadataEntries = getMetadataEntries(log.metadata);

                return (
                  <React.Fragment key={log.id}>
                    <TableBodyRow
                      clickable
                      expanded={isExpanded}
                      onClick={() => toggleExpandedRow(log.id)}
                    >
                      <DataCell>{moment(log.created_at).format("MMM D, YYYY h:mm a")}</DataCell>
                      <DataCell>{log.actor?.name || log.actor?.email}</DataCell>
                      <DataCell>{actionLabels[log.action_type] || log.action_type}</DataCell>
                      <DataCell>{getTargetSummary(log)}</DataCell>
                    </TableBodyRow>
                    <ExpandedRow>
                      <ExpandedCell colSpan={4} open={isExpanded}>
                        <ExpandContent open={isExpanded}>
                          {nonClientTargets.length > 0 && (
                            <DetailSection>
                              <SectionHeader details>Details</SectionHeader>
                              <TargetList>
                                {nonClientTargets.map((target, index) => (
                                  <TargetListItem key={`${log.id}-${target.type}-${target.id || index}`}>
                                    <strong>{getTargetTypeLabel(target.type)}:</strong> {target.label || `${getTargetTypeLabel(target.type)} ${target.id || ""}`.trim()}
                                  </TargetListItem>
                                ))}
                              </TargetList>
                            </DetailSection>
                          )}
                          {metadataEntries.length > 0 && (
                            <DetailSection>
                              <TargetList>
                                {metadataEntries.map(([key, value]) => (
                                  <TargetListItem key={`${log.id}-meta-${key}`}>
                                    <strong>{formatMetadataLabel(key)}:</strong> {formatMetadataValue(key, value, log.metadata)}
                                  </TargetListItem>
                                ))}
                              </TargetList>
                            </DetailSection>
                          )}
                          {clientTargets.length > 0 && (
                            <DetailSection>
                              <SectionHeader>{clientTargets.length === 1 ? "Client" : "Clients"}</SectionHeader>
                              <TargetList>
                                {clientTargets.map((target, index) => (
                                  <TargetListItem key={`${log.id}-client-${target.id || index}`}>
                                    {target.label || "Client"}
                                  </TargetListItem>
                                ))}
                              </TargetList>
                            </DetailSection>
                          )}
                        </ExpandContent>
                      </ExpandedCell>
                    </ExpandedRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </>
  );
};

export default LogTable;
